import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip,
  CircularProgress, Alert, Snackbar, Skeleton, IconButton, Tooltip,
} from '@mui/material';
import {
  TrendingUp, Refresh, PlayArrow, CheckCircle, Warning,
  Timeline, BarChart as BarChartIcon, Assessment, Add,
} from '@mui/icons-material';
import {
  Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, Legend, ResponsiveContainer, ReferenceLine,
  Area, AreaChart, ComposedChart,
} from 'recharts';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1').replace(/\/api\/v\d+$/, '');

function apiFetch(path: string, opts: RequestInit = {}) {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  return fetch(`${API_BASE}/api/v1${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface ForecastResult {
  itemId: string;
  itemCode?: string;
  forecastedDemand: number;
  confidence: number;
  method: string;
  dataPoints: number;
  parameters: { periods?: number; alpha?: number };
  upperBound?: number;
  lowerBound?: number;
}

interface MpsRecord {
  id: string;
  itemId: string;
  itemCode: string;
  itemDescription: string;
  planningPeriodStart: string;
  planningPeriodEnd: string;
  planningHorizon: string;
  demandForecast: number;
  actualDemand: number;
  plannedProduction: number;
  actualProduction: number;
  endingInventory: number;
  safetyStock: number;
  status: string;
  unit: string;
}

interface ForecastStat { label: string; value: string | number; color: string; icon: React.ReactNode }

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon }: ForecastStat) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
        <Typography variant="h5" fontWeight="bold" sx={{ color }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const DemandForecasting: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mpsList, setMpsList] = useState<MpsRecord[]>([]);
  const [forecastResult, setForecastResult] = useState<ForecastResult | null>(null);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [forecastMethod, setForecastMethod] = useState<'MA' | 'ES'>('MA');
  const [periods, setPeriods] = useState(3);
  const [alpha, setAlpha] = useState(0.3);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });
  const [applying, setApplying] = useState(false);

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, msg, severity });

  // Load MPS records to populate item list
  const loadMps = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/planning/mps');
      if (r.ok) {
        const d = await r.json();
        setMpsList(d.data || d || []);
      }
    } catch {
      // no data yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMps(); }, [loadMps]);

  // Run forecast
  const runForecast = useCallback(async () => {
    if (!selectedItemId) { showSnack('Select an item first', 'error'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ method: forecastMethod });
      if (forecastMethod === 'MA') params.set('window', String(periods));
      if (forecastMethod === 'ES') params.set('alpha', String(alpha));
      const r = await apiFetch(`/planning/mps/forecast/${selectedItemId}?${params}`);
      if (r.ok) {
        const d = await r.json();
        setForecastResult(d.data || d);
        showSnack('Forecast generated successfully');
      } else {
        showSnack('Failed to generate forecast', 'error');
      }
    } catch {
      showSnack('Error generating forecast', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedItemId, forecastMethod, periods, alpha]);

  // Apply forecast to a specific MPS record
  const applyForecast = useCallback(async (mpsId: string) => {
    if (!forecastResult) return;
    setApplying(true);
    try {
      const r = await apiFetch(`/planning/mps/${mpsId}/apply-forecast`, {
        method: 'POST',
        body: JSON.stringify({
          method: forecastMethod,
          window: forecastMethod === 'MA' ? periods : undefined,
          alpha: forecastMethod === 'ES' ? alpha : undefined,
        }),
      });
      if (r.ok) {
        showSnack('Forecast applied to MPS record');
        loadMps();
      } else {
        showSnack('Failed to apply forecast', 'error');
      }
    } catch {
      showSnack('Error applying forecast', 'error');
    } finally {
      setApplying(false);
    }
  }, [alpha, forecastMethod, forecastResult, loadMps, periods]);

  // Build chart data from MPS records for selected item
  const chartData = mpsList
    .filter(m => !selectedItemId || m.itemId === selectedItemId)
    .sort((a, b) => a.planningPeriodStart.localeCompare(b.planningPeriodStart))
    .map(m => ({
      period: m.planningPeriodStart.slice(0, 10),
      actual: Number(m.actualDemand) || 0,
      forecast: Number(m.demandForecast) || 0,
      planned: Number(m.plannedProduction) || 0,
      inventory: Number(m.endingInventory) || 0,
    }));

  // Accuracy metrics
  const accuracy = (() => {
    const pairs = chartData.filter(d => d.actual > 0 && d.forecast > 0);
    if (!pairs.length) return null;
    const mape = pairs.reduce((s, d) => s + Math.abs((d.actual - d.forecast) / d.actual), 0) / pairs.length * 100;
    const bias = pairs.reduce((s, d) => s + (d.forecast - d.actual), 0) / pairs.length;
    const rmse = Math.sqrt(pairs.reduce((s, d) => s + Math.pow(d.actual - d.forecast, 2), 0) / pairs.length);
    return { mape: mape.toFixed(1), bias: bias.toFixed(1), rmse: rmse.toFixed(1), samples: pairs.length };
  })();

  // Unique items for selector
  const items = Array.from(new Map(mpsList.map(m => [m.itemId, m])).values());

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Demand Forecasting</Typography>
        <IconButton onClick={loadMps} disabled={loading}><Refresh /></IconButton>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total MPS Records" value={mpsList.length} color="#1976d2" icon={<Assessment />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Items Tracked" value={items.length} color="#388e3c" icon={<TrendingUp />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Forecast MAPE"
            value={accuracy ? `${accuracy.mape}%` : '—'}
            color={accuracy && parseFloat(accuracy.mape) < 10 ? '#388e3c' : '#f57c00'}
            icon={<Timeline />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Latest Forecast"
            value={forecastResult ? forecastResult.forecastedDemand.toFixed(0) : '—'}
            color="#7b1fa2"
            icon={<BarChartIcon />}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Forecast Engine" />
          <Tab label="Historical Analysis" />
          <Tab label="Accuracy Metrics" />
          <Tab label="MPS Records" />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Forecast Engine ── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Forecast Parameters</Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Item</InputLabel>
                <Select value={selectedItemId} onChange={e => setSelectedItemId(e.target.value)} label="Item">
                  <MenuItem value="">All Items</MenuItem>
                  {items.map(m => (
                    <MenuItem key={m.itemId} value={m.itemId}>{m.itemCode} — {m.itemDescription}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Algorithm</InputLabel>
                <Select value={forecastMethod} onChange={e => setForecastMethod(e.target.value as any)} label="Algorithm">
                  <MenuItem value="MA">Moving Average (MA)</MenuItem>
                  <MenuItem value="ES">Exponential Smoothing (ES)</MenuItem>
                </Select>
              </FormControl>

              {forecastMethod === 'MA' && (
                <TextField fullWidth label="Periods (N)" type="number" value={periods}
                  onChange={e => setPeriods(Number(e.target.value))} sx={{ mb: 2 }}
                  inputProps={{ min: 2, max: 12 }} helperText="Number of past periods to average" />
              )}

              {forecastMethod === 'ES' && (
                <TextField fullWidth label="Alpha (α)" type="number" value={alpha}
                  onChange={e => setAlpha(Number(e.target.value))} sx={{ mb: 2 }}
                  inputProps={{ min: 0.1, max: 0.9, step: 0.05 }} helperText="Smoothing factor (0.1–0.9)" />
              )}

              <Button fullWidth variant="contained" startIcon={<PlayArrow />}
                onClick={runForecast} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Run Forecast'}
              </Button>
            </Paper>

            {forecastResult && (
              <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" gutterBottom>Forecast Result</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Method</Typography>
                    <Chip size="small" label={forecastResult.method.replace('_', ' ').toUpperCase()} color="primary" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Forecasted Demand</Typography>
                    <Typography fontWeight="bold">{forecastResult.forecastedDemand.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Confidence</Typography>
                    <Typography fontWeight="bold" color={forecastResult.confidence > 0.7 ? 'success.main' : 'warning.main'}>
                      {(forecastResult.confidence * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Data Points</Typography>
                    <Typography>{forecastResult.dataPoints}</Typography>
                  </Box>
                  {forecastResult.upperBound && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Range</Typography>
                      <Typography>{forecastResult.lowerBound?.toFixed(1)} – {forecastResult.upperBound.toFixed(1)}</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Demand vs Forecast Trend</Typography>
              {loading ? <Skeleton variant="rectangular" height={300} /> : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <RechartTooltip />
                    <Legend />
                    <Area type="monotone" dataKey="actual" fill="#e3f2fd" stroke="#1976d2" name="Actual Demand" />
                    <Line type="monotone" dataKey="forecast" stroke="#f57c00" strokeWidth={2} strokeDasharray="5 5" name="Forecast" dot={false} />
                    {forecastResult && (
                      <ReferenceLine y={forecastResult.forecastedDemand} stroke="#7b1fa2" strokeDasharray="8 4"
                        label={{ value: `Next: ${forecastResult.forecastedDemand.toFixed(0)}`, position: 'right', fontSize: 11 }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                  <Typography color="text.secondary">No MPS data. Add records in the Planning module first.</Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" gutterBottom>Production Plan vs Inventory</Typography>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <RechartTooltip />
                    <Legend />
                    <Bar dataKey="planned" name="Planned Production" fill="#388e3c" />
                    <Bar dataKey="inventory" name="Ending Inventory" fill="#7b1fa2" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={220} />}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Tab 1: Historical Analysis ── */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Historical Demand Analysis</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Item</InputLabel>
                <Select value={selectedItemId} onChange={e => setSelectedItemId(e.target.value)} label="Filter by Item">
                  <MenuItem value="">All Items</MenuItem>
                  {items.map(m => <MenuItem key={m.itemId} value={m.itemId}>{m.itemCode}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {loading ? <Skeleton variant="rectangular" height={360} /> : (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartTooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="actual" name="Actual Demand" fill="#1976d2" opacity={0.8} />
                <Bar yAxisId="left" dataKey="forecast" name="Demand Forecast" fill="#f57c00" opacity={0.8} />
                <Line yAxisId="right" type="monotone" dataKey="inventory" name="Inventory" stroke="#7b1fa2" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Paper>
      )}

      {/* ── Tab 2: Accuracy Metrics ── */}
      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Forecast Accuracy Metrics</Typography>
              {accuracy ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { label: 'MAPE (Mean Absolute % Error)', value: `${accuracy.mape}%`, good: parseFloat(accuracy.mape) < 10, desc: '<10% is excellent' },
                    { label: 'Forecast Bias', value: accuracy.bias, good: Math.abs(parseFloat(accuracy.bias)) < 5, desc: 'Close to 0 is ideal' },
                    { label: 'RMSE', value: accuracy.rmse, good: true, desc: 'Root Mean Squared Error' },
                    { label: 'Sample Size', value: accuracy.samples, good: accuracy.samples >= 6, desc: '6+ periods recommended' },
                  ].map(m => (
                    <Box key={m.label} sx={{ p: 2, border: '1px solid', borderColor: m.good ? 'success.light' : 'warning.light', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{m.desc}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" fontWeight="bold">{m.value}</Typography>
                          {m.good ? <CheckCircle color="success" fontSize="small" /> : <Warning color="warning" fontSize="small" />}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">Run a forecast and ensure there is historical demand + forecast data to compute accuracy metrics.</Alert>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Forecast Error Over Time</Typography>
              {chartData.filter(d => d.actual > 0).length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData.filter(d => d.actual > 0).map(d => ({
                    ...d, error: d.actual > 0 ? ((d.forecast - d.actual) / d.actual * 100) : 0,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis unit="%" />
                    <RechartTooltip formatter={(v: any) => [`${Number(v).toFixed(1)}%`, 'Error %']} />
                    <ReferenceLine y={0} stroke="#666" />
                    <Area type="monotone" dataKey="error" stroke="#f57c00" fill="#fff3e0" name="Forecast Error %" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={300} />}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── Tab 3: MPS Records ── */}
      {tab === 3 && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Item Code</TableCell>
                  <TableCell>Period Start</TableCell>
                  <TableCell>Horizon</TableCell>
                  <TableCell align="right">Actual Demand</TableCell>
                  <TableCell align="right">Forecasted</TableCell>
                  <TableCell align="right">Planned Prod.</TableCell>
                  <TableCell align="right">Safety Stock</TableCell>
                  <TableCell>Status</TableCell>
                  {forecastResult && <TableCell align="center">Apply</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>{[...Array(8)].map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                  ))
                ) : mpsList.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No MPS records. Add plans in the Planning module.</Typography>
                  </TableCell></TableRow>
                ) : (
                  mpsList.map(m => {
                    const deviation = m.actualDemand > 0
                      ? Math.abs((m.demandForecast - m.actualDemand) / m.actualDemand * 100)
                      : 0;
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell><Typography variant="body2" fontWeight="medium">{m.itemCode}</Typography></TableCell>
                        <TableCell>{m.planningPeriodStart?.slice(0, 10)}</TableCell>
                        <TableCell><Chip size="small" label={m.planningHorizon} /></TableCell>
                        <TableCell align="right">{Number(m.actualDemand).toFixed(0)} {m.unit}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {Number(m.demandForecast).toFixed(0)}
                            {deviation > 15 && <Warning fontSize="small" color="warning" />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{Number(m.plannedProduction).toFixed(0)}</TableCell>
                        <TableCell align="right">{Number(m.safetyStock).toFixed(0)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={m.status}
                            color={m.status === 'active' ? 'success' : m.status === 'approved' ? 'primary' : 'default'} />
                        </TableCell>
                        {forecastResult && (
                          <TableCell align="center">
                            <Tooltip title={`Apply forecast ${forecastResult.forecastedDemand.toFixed(0)} to this record`}>
                              <IconButton size="small" onClick={() => applyForecast(m.id)} disabled={applying}>
                                <Add fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DemandForecasting;
